import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, Search, RefreshCw, CreditCard, ExternalLink, Copy, X } from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { adminAPI } from '@/utils/adminApi';
import { formatCOP } from '@/utils/format';

const STATUS_META = {
  intent_created: { label: 'Link creado', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  processing: { label: 'Procesando', className: 'bg-purple-50 text-purple-700 border-purple-200' },
  approved: { label: 'Aprobado', className: 'bg-green-50 text-green-800 border-green-200' },
  rejected: { label: 'Rechazado', className: 'bg-red-50 text-red-700 border-red-200' },
  failed: { label: 'Fallido', className: 'bg-red-50 text-red-700 border-red-200' },
  pending: { label: 'Pendiente', className: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [envFilter, setEnvFilter] = useState('');
  const [search, setSearch] = useState('');
  const [refreshingRef, setRefreshingRef] = useState(null);
  const [detail, setDetail] = useState(null);

  const fetchPayments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.listPayments({ status: statusFilter, environment: envFilter, search, limit: 100 });
      setPayments(data.payments || []);
      setTotal(data.total || 0);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [statusFilter, envFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchPayments, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchPayments, search]);

  const refreshOne = async (reference) => {
    setRefreshingRef(reference);
    try {
      const data = await adminAPI.refreshPayment(reference);
      // Update the row in place
      setPayments((prev) => prev.map((p) => p.reference === reference ? { ...p, ...(data.payment || {}) } : p));
      if (detail?.reference === reference) setDetail({ ...detail, ...(data.payment || {}), bold: data.bold });
    } catch (e) {
      alert(e.response?.data?.detail || 'Error al consultar Bold');
    } finally {
      setRefreshingRef(null);
    }
  };

  const copyRef = (ref) => {
    navigator.clipboard.writeText(ref);
  };

  return (
    <AdminLayout>
      <div className="max-w-7xl mx-auto">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Panel administrativo</p>
            <h1 className="font-display font-bold text-3xl sm:text-4xl text-[#1C1917] tracking-tight" data-testid="admin-payments-title">Conciliación Bold</h1>
            <p className="text-[#57534E] mt-2">{total} {total === 1 ? 'transacción' : 'transacciones'} • Consulta el estado en tiempo real</p>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl border border-[#E7E5E4] p-4 mb-6 grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por referencia, link ID, orden..."
              className="w-full pl-10 pr-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
              data-testid="admin-payments-search"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="admin-payments-status-filter"
          >
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select
            value={envFilter}
            onChange={(e) => setEnvFilter(e.target.value)}
            className="px-4 py-2.5 rounded-full border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43]"
            data-testid="admin-payments-env-filter"
          >
            <option value="">Todos los ambientes</option>
            <option value="production">Producción</option>
            <option value="sandbox">Sandbox</option>
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-3xl border border-[#E7E5E4] overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" /></div>
          ) : payments.length === 0 ? (
            <div className="text-center py-16" data-testid="admin-payments-empty">
              <CreditCard className="w-12 h-12 text-[#8BA58F] mx-auto mb-4" />
              <p className="text-[#57534E]">No hay pagos con esos filtros</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-sm" data-testid="admin-payments-table">
                <thead className="bg-[#FAF9F5] text-xs uppercase tracking-widest text-[#8BA58F]">
                  <tr>
                    <th className="text-left px-6 py-3">Referencia / Fecha</th>
                    <th className="text-left px-6 py-3">Orden</th>
                    <th className="text-left px-6 py-3">Cliente</th>
                    <th className="text-right px-6 py-3">Monto</th>
                    <th className="text-center px-6 py-3">Estado</th>
                    <th className="text-center px-6 py-3">Bold</th>
                    <th className="text-center px-6 py-3">Ambiente</th>
                    <th className="text-right px-6 py-3">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#F5F5F4]">
                  {payments.map((p) => {
                    const st = STATUS_META[p.status] || STATUS_META.pending;
                    return (
                      <tr key={p.reference || p.id} className="hover:bg-[#FAF9F5]" data-testid={`admin-payment-row-${p.reference}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 group">
                            <span className="font-medium text-[#1C1917] text-xs">{p.reference}</span>
                            <button onClick={() => copyRef(p.reference)} className="opacity-0 group-hover:opacity-100 transition-opacity text-[#8BA58F] hover:text-[#2A5C43]" title="Copiar">
                              <Copy className="w-3 h-3" />
                            </button>
                          </div>
                          <div className="text-xs text-[#57534E]">{fmtDate(p.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 text-[#1C1917]">{p.order_number || '—'}</td>
                        <td className="px-6 py-4">
                          <p className="text-[#1C1917]">{p.customer_name || '—'}</p>
                          <p className="text-xs text-[#57534E]">{p.customer_email}</p>
                        </td>
                        <td className="px-6 py-4 text-right font-display font-bold text-[#1C1917]">{formatCOP(p.amount)}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`inline-block px-2.5 py-1 rounded-full text-[10px] font-semibold border ${st.className}`}>
                            {st.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center text-xs text-[#57534E]">{p.last_bold_status || '—'}</td>
                        <td className="px-6 py-4 text-center">
                          <span className={`text-[10px] font-semibold uppercase tracking-widest ${
                            p.environment === 'production' ? 'text-[#2A5C43]' : 'text-[#D47A4A]'
                          }`}>{p.environment || 'sandbox'}</span>
                        </td>
                        <td className="px-6 py-4 text-right space-x-2">
                          <button
                            onClick={() => refreshOne(p.reference)}
                            disabled={refreshingRef === p.reference}
                            className="text-[#2A5C43] hover:underline text-xs font-medium inline-flex items-center gap-1 disabled:opacity-50"
                            data-testid={`refresh-payment-${p.reference}`}
                          >
                            {refreshingRef === p.reference ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                            Consultar
                          </button>
                          <button
                            onClick={() => setDetail(p)}
                            className="text-[#1C1917] hover:underline text-xs font-medium"
                            data-testid={`view-payment-${p.reference}`}
                          >
                            Detalle
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail modal */}
        {detail && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setDetail(null)} data-testid="admin-payment-detail-modal">
            <div className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
              <div className="p-6 border-b border-[#F5F5F4] flex items-start justify-between">
                <div>
                  <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-1">Pago Bold</p>
                  <h2 className="font-display font-bold text-xl text-[#1C1917] break-all">{detail.reference}</h2>
                </div>
                <button onClick={() => setDetail(null)} className="p-2 hover:bg-[#F7F2E8] rounded-full">
                  <X className="w-5 h-5 text-[#57534E]" />
                </button>
              </div>
              <div className="p-6 space-y-4 max-h-[70vh] overflow-y-auto text-sm">
                <div className="grid grid-cols-2 gap-4">
                  <Field label="Estado interno" value={detail.status} />
                  <Field label="Estado en Bold" value={detail.last_bold_status || '—'} />
                  <Field label="Ambiente" value={detail.environment} />
                  <Field label="Monto" value={formatCOP(detail.amount)} />
                  <Field label="Orden" value={detail.order_number || detail.order_id} />
                  <Field label="Bold Link ID" value={detail.bold_link_id || '—'} />
                  {detail.bold_transaction_id && <Field label="Bold Txn ID" value={detail.bold_transaction_id} />}
                  <Field label="Creado" value={fmtDate(detail.created_at)} />
                </div>
                {detail.bold_link_url && (
                  <a href={detail.bold_link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm text-[#2A5C43] hover:underline" data-testid="payment-checkout-link">
                    <ExternalLink className="w-4 h-4" /> Abrir link de pago
                  </a>
                )}
                <div className="pt-2 flex justify-end">
                  <button
                    onClick={() => refreshOne(detail.reference)}
                    disabled={refreshingRef === detail.reference}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold bg-[#2A5C43] text-white hover:bg-[#214A35] disabled:opacity-50"
                  >
                    {refreshingRef === detail.reference ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                    Consultar estado en Bold
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

const Field = ({ label, value }) => (
  <div>
    <p className="text-[10px] uppercase tracking-widest text-[#8BA58F] font-semibold mb-0.5">{label}</p>
    <p className="font-medium text-[#1C1917] break-all">{value}</p>
  </div>
);

export default AdminPayments;
